import React, { useState, useEffect } from 'react';
import authClient from '../auth/authClient';

// Edit Profile Modal Component
export const EditProfileModal = ({ user, onClose, onSubmit, loading }) => {
  const currentUser = authClient.getUser();
  const isSuperAdmin = !!currentUser?.isSuperAdmin;
  const canToggleSuperAdmin = isSuperAdmin && user?.id !== currentUser?.id;

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    is_super_admin: user?.isSuperAdmin || false,
    // V274 — the verification stamps as booleans for the form; the server
    // turns true into NOW() and false into NULL. Prefilled from the
    // timestamps so an untouched toggle round-trips the current state.
    email_verified: !!user?.emailVerifiedAt,
    phone_verified: !!user?.phoneVerifiedAt,
  });

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { name: formData.name, phone: formData.phone };
    if (isSuperAdmin) {
      // Identity-level edits are super-admin acts, enforced again server-side.
      if (formData.email !== (user?.email || '')) payload.email = formData.email;
      payload.email_verified = formData.email_verified;
      payload.phone_verified = formData.phone_verified;
    }
    if (canToggleSuperAdmin) {
      payload.is_super_admin = formData.is_super_admin;
    }
    onSubmit(payload);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Profile</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Email Address</label>
              {isSuperAdmin ? (
                <>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value, email_verified: e.target.value === (user?.email || '') ? formData.email_verified : false})}
                    placeholder="name@company.com"
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: 6 }}>
                    <input
                      type="checkbox"
                      checked={formData.email_verified}
                      onChange={e => setFormData({...formData, email_verified: e.target.checked})}
                      style={{ width: 16, height: 16, accentColor: '#228b22' }}
                    />
                    <span style={{ fontSize: '0.85rem' }}>Email verified</span>
                    {user?.emailVerifiedAt && (
                      <span className="form-hint" style={{ margin: 0 }}>
                        (last verified {new Date(user.emailVerifiedAt).toLocaleDateString()})
                      </span>
                    )}
                  </label>
                  <span className="form-hint">Changing the email clears its verified mark unless re-ticked — an admin vouching for a value they just confirmed.</span>
                </>
              ) : (
                <>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="input-disabled"
                  />
                  <span className="form-hint">Only super admins can change email</span>
                </>
              )}
            </div>
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value, phone_verified: e.target.value === (user?.phone || '') ? formData.phone_verified : false})}
                placeholder="+91 9876543210"
              />
              <span className="form-hint">10-digit Indian mobiles are read as +91; other countries need the code</span>
              {isSuperAdmin && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={formData.phone_verified}
                    onChange={e => setFormData({...formData, phone_verified: e.target.checked})}
                    style={{ width: 16, height: 16, accentColor: '#228b22' }}
                  />
                  <span style={{ fontSize: '0.85rem' }}>Mobile verified</span>
                  {user?.phoneVerifiedAt && (
                    <span className="form-hint" style={{ margin: 0 }}>
                      (last verified {new Date(user.phoneVerifiedAt).toLocaleDateString()})
                    </span>
                  )}
                </label>
              )}
            </div>
            {canToggleSuperAdmin && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_super_admin}
                    onChange={e => setFormData({...formData, is_super_admin: e.target.checked})}
                    style={{ width: 18, height: 18, accentColor: '#228b22' }}
                  />
                  <span>Super Admin</span>
                </label>
                <span className="form-hint" style={{ color: '#c62828' }}>
                  Super admins have unrestricted access to all tenants and features
                </span>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
export const DeleteConfirmModal = ({ user, onClose, onConfirm, loading }) => {
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Delete User?</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>This will remove <strong>{user?.name || user?.email}</strong> from the system.</p>
          <ul className="warning-list">
            <li>User will be logged out immediately</li>
            <li>Tenant assignments removed</li>
            <li>This action cannot be undone</li>
          </ul>
          <div className="form-group">
            <label>Type "DELETE" to confirm:</label>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn btn-danger" 
            onClick={onConfirm}
            disabled={confirmText !== 'DELETE' || loading}
          >
            {loading ? 'Deleting...' : 'Delete User'}
          </button>
        </div>
      </div>
    </div>
  );
};
